import { useState } from 'react'
import { DeleteOutlined } from '../../../components/icons'
import { Button } from '../../../components/ui/Button'
import { formatDate } from '../../workspaces/format'
import { UPLOAD_SESSION_STATUS_COLOR, UPLOAD_SESSION_STATUS_LABEL } from '../types'
import type { UploadSessionSummary } from '../types'

/** Cap for the stacked thumbnail strip, applied to BOTH the resolved-URL and
 * the gray placeholder branch (UID-540). The design handoff renders at most
 * four tiles and no `+N` overflow indicator — `Fictiv Platform.dc.html:9204`,
 * `thumbs: rows.slice(0, 4)`; `docs/screens/uploads.png` shows a
 * "46 files · 30 parts" session with exactly four, the true totals being
 * disclosed by the meta line instead. Previously this capped only the
 * placeholder branch, so a session whose thumbnails had resolved rendered one
 * tile per file (46 of them) and overran the row. Holding both branches to the
 * same cap also stops the strip changing width as `UploadsPage`'s poll fills
 * `thumbnailUrls` in. */
const THUMB_MAX = 4

/** Coarse, status-based progress-bar fallback for sessions the BFF hasn't
 * reported a real `progressPct` for yet (types.ts's doc comment) — a visual
 * approximation only, never surfaced to the user as an exact number. */
const STATUS_FALLBACK_PCT: Record<UploadSessionSummary['status'], number> = {
  uploading: 15,
  identifying: 60,
  ready: 100,
  confirmed: 100,
}

export function progressPctFor(session: UploadSessionSummary): number {
  return session.progressPct ?? STATUS_FALLBACK_PCT[session.status]
}

export interface InProgressSessionCardProps {
  readonly session: UploadSessionSummary
  readonly onConfirm: (sessionId: string) => Promise<void>
  readonly onResume: (sessionId: string) => void
  readonly onDelete: (sessionId: string) => Promise<void>
}

/**
 * "In progress" session row (UID-140 S4, `Fictiv Platform.dc.html:2472-
 * 2489`): stacked thumbnail cluster, name + meta, progress bar with a
 * text-carried status label, and Confirm/Resume review/Delete actions.
 * Delete fires immediately, matching the design's `onDelete`
 * (`dc.html:9207`) — no confirmation dialog. Confirm/Delete failures
 * (expected today — see `useUploads.ts`'s TODO(UID-141) stubs) surface
 * inline rather than throwing, so a demo user gets an honest message
 * instead of a silent no-op or a crash.
 */
export function InProgressSessionCard({
  session,
  onConfirm,
  onResume,
  onDelete,
}: InProgressSessionCardProps) {
  const [confirming, setConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const thumbs = session.thumbnailUrls?.length
    ? session.thumbnailUrls.slice(0, THUMB_MAX)
    : Array.from({ length: Math.min(session.fileCount, THUMB_MAX) }, () => null)

  async function handleConfirm() {
    setActionError(null)
    setConfirming(true)
    try {
      await onConfirm(session.sessionId)
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Couldn’t confirm this upload.')
    } finally {
      setConfirming(false)
    }
  }

  async function handleDelete() {
    setActionError(null)
    setDeleting(true)
    try {
      await onDelete(session.sessionId)
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Couldn’t delete this upload.')
    } finally {
      setDeleting(false)
    }
  }

  const pct = progressPctFor(session)
  const isReady = session.status === 'ready'

  return (
    <div className="card uploads-session-card">
      <div className="uploads-session-thumbs" aria-hidden="true">
        {thumbs.map((url, index) => (
          <span
            key={url ?? index}
            className="uploads-session-thumb"
            // Security/code-review follow-up (UID-140 Gate 5, F1): quote the
            // URL inside `url(...)` and escape only quote/backslash — NOT
            // `encodeURI`, which double-encodes the `%`-escapes already
            // present in a presigned S3 URL's query string (signature, date,
            // etc.), corrupting the signature so S3 rejects the request and
            // the thumbnail silently never loads (this WAS the bug: every
            // other surface renders these same URLs via a plain `<img src>`,
            // which doesn't re-encode them — only this CSS-background path
            // did). Quoting + escaping just the quote/backslash still closes
            // the original `)`-breakout concern without touching the URL's
            // own valid encoding. `thumbnailUrls` is real and populated today
            // (UID-330/UID-335 — `GET /sessions` resolves each session's file
            // thumbnails inline; see `uploads-routes.ts`).
            style={url ? { backgroundImage: `url("${url.replace(/["\\]/g, '\\$&')}")` } : undefined}
          />
        ))}
      </div>
      <div className="uploads-session-info">
        <p className="uploads-session-name">{session.name}</p>
        <p className="uploads-session-meta">
          {session.fileCount} file{session.fileCount === 1 ? '' : 's'}
          {session.partCount != null
            ? ` · ${session.partCount} part${session.partCount === 1 ? '' : 's'}`
            : ''}{' '}
          · started {formatDate(session.createdAt)}
        </p>
        {actionError && (
          <p role="alert" className="uploads-session-action-error">
            {actionError}
          </p>
        )}
      </div>
      <div className="uploads-session-progress">
        <div className="uploads-session-progress-track">
          <div className="uploads-session-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <p
          className="uploads-session-status-label"
          style={{ color: UPLOAD_SESSION_STATUS_COLOR[session.status] }}
        >
          {UPLOAD_SESSION_STATUS_LABEL[session.status]}
        </p>
      </div>
      <div className="uploads-session-actions">
        {isReady && (
          <Button
            variant="outline"
            size="sm"
            loading={confirming}
            onClick={() => void handleConfirm()}
          >
            Confirm
          </Button>
        )}
        <Button size="sm" onClick={() => onResume(session.sessionId)}>
          Resume review
        </Button>
        <button
          type="button"
          className="uploads-trash-btn"
          title="Delete upload"
          aria-label={`Delete ${session.name}`}
          disabled={deleting}
          onClick={() => void handleDelete()}
        >
          <DeleteOutlined aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}
